import type { Feedback, FeedbackAction, FeedbackData } from '#shared/types/Feedback'
import { useApi } from '@/composables/api/useApi'

/**
 * Composable for feedback-related functionality
 */
export function useFeedback() {
    const { api } = useApi()

    /**
     * Bola už na túto (action, requestId) dvojicu daná spätná väzba?
     *
     * Ide cez API zámerne. `feedbacks` nesú voľné texty a hodnotenia, takže
     * pravidlá ich klientovi nedávajú čítať — a príjemca NPS e-mailu býva
     * odhlásený, takže priamy dopyt do Firestore by mu zlyhal a odkaz by
     * vyzeral ako neplatný.
     */
    async function feedbackExistsForRequest(action: FeedbackAction, requestId: string): Promise<boolean> {
        const response = await api.get(`/feedback/exists`, { params: { action, requestId } })
        return response.data.exists === true
    }

    /**
     * Saves a new feedback
     */
    async function saveFeedback(data: Partial<Feedback>): Promise<string> {
        return (await api.post(`/feedback/save`, data)).data.id
    }

    /**
     * Updates an existing feedback - used to attach the free-text answer to a score
     * that was already stored when the user clicked it.
     */
    const updateFeedback = async (feedbackId: string, data: Partial<Feedback>): Promise<void> => {
        await api.post(`/feedback/${feedbackId}/update`, data)
    }

    /**
     * Checks if feedback was already given
     */
    async function wasFeedbackGivenAlready(data: FeedbackData): Promise<boolean> {
        // Callers coming from an e-mail link only carry `requestId`; `request` is set when
        // the caller already has the loaded document. Reading `data.request.id` blindly threw
        // for every logged-out recipient, which is most of them.
        const requestId = data.requestId ?? data.request?.id

        if (!requestId) {
            return false
        }

        return await feedbackExistsForRequest(data.action, requestId)
    }

    return {
        feedbackExistsForRequest,
        saveFeedback,
        updateFeedback,
        wasFeedbackGivenAlready
    }
}
